import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from '#app/components/ui/input-group.tsx'
import { useHelpDialogContext } from '#app/routes/mocra+/_form+/help-dialog-context.tsx'

const DEFAULT_STORAGE_KEY = 'mocra-form-auth'
const DEFAULT_AUTH_TOKEN = 'bW9jcmEtZm9ybS1hdXRoLXRva2VuLTIwMjU='

type Props = {
	readonly children: ReactNode
	readonly storageKey?: string
	readonly authTokenBase64?: string
}

function getStoredAuth(storageKey: string): string | null {
	if (typeof window === 'undefined') return null
	try {
		return window.localStorage.getItem(storageKey)
	} catch {
		return null
	}
}

function setStoredAuth(storageKey: string, token: string): void {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.setItem(storageKey, token)
	} catch {
		// Ignore storage errors
	}
}

function validatePassword(password: string, authTokenBase64: string): boolean {
	try {
		const encoded = btoa(password)
		return encoded === authTokenBase64
	} catch {
		return false
	}
}

export function ProtectedPage({
	children,
	storageKey = DEFAULT_STORAGE_KEY,
	authTokenBase64 = DEFAULT_AUTH_TOKEN,
}: Props) {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
	const [password, setPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const helpDialog = useHelpDialogContext()

	useEffect(() => {
		const stored = getStoredAuth(storageKey)
		setIsAuthenticated(stored === authTokenBase64)
	}, [storageKey, authTokenBase64])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setError(null)

		if (!password.trim()) {
			setError('Please enter a password')
			return
		}

		if (validatePassword(password, authTokenBase64)) {
			const token = btoa(password)
			setStoredAuth(storageKey, token)
			setIsAuthenticated(true)
			setPassword('')
		} else {
			setError('Incorrect password. Please try again.')
			setPassword('')
		}
	}

	// Show nothing while checking auth state
	if (isAuthenticated === null) {
		return null
	}

	// Show protected content if authenticated
	if (isAuthenticated) {
		return <>{children}</>
	}

	// Show password gate with blurred content
	return (
		<div className="relative">
			{/* Blurred content - only the form is blurred */}
			<div className="relative">
				<div className="pointer-events-none select-none" aria-hidden="true">
					{children}
				</div>

				{/* Password input - sticky in the middle */}
				<div className="absolute inset-0 flex justify-center rounded-xl backdrop-blur-xs">
					<div className="sticky top-1/3 z-10 h-fit w-full max-w-sm space-y-3 rounded-2xl border-2 border-neutral-200 bg-white p-6">
						<p className="text-muted-foreground text-center text-xs font-medium">
							Enter password to access form
						</p>
						<form onSubmit={handleSubmit} className="space-y-2">
							<InputGroup>
								<InputGroupInput
									id="password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									autoFocus
									autoComplete="off"
									className="text-center"
								/>
								<InputGroupAddon align="inline-end">
									<InputGroupButton
										type="button"
										size="icon-xs"
										onClick={() => setShowPassword(!showPassword)}
										aria-label={
											showPassword ? 'Hide password' : 'Show password'
										}
									>
										{showPassword ? (
											<EyeOff className="h-4 w-4" aria-hidden="true" />
										) : (
											<Eye className="h-4 w-4" aria-hidden="true" />
										)}
									</InputGroupButton>
								</InputGroupAddon>
							</InputGroup>
							{error ? (
								<p className="text-destructive text-center text-xs">{error}</p>
							) : null}
							<Button type="submit" className="w-full text-xs" size="sm">
								Unlock
							</Button>
							{helpDialog ? (
								<Button
									type="button"
									variant="link"
									className="w-full text-xs"
									size="sm"
									onClick={helpDialog.openHelpDialog}
								>
									Request access
								</Button>
							) : null}
						</form>
					</div>
				</div>
			</div>
		</div>
	)
}
